import BaseTool from './BaseTool.js';
import Transform from '../../math/Transform.js';
import Material from '../../materials/Material.js';

/**
 * Tool for placing new cuboids with drag-to-size support
 * Click on a face to place adjacent to it (1x1x1)
 * Click and drag to extend in the face plane (variable size)
 * Click on empty space to place on ground plane
 */
export default class AddTool extends BaseTool {
  constructor(editor) {
    super(editor);
    this.previewPosition = null;

    // Drag-to-size state
    this.isDragging = false;
    this.dragStartPosition = null;  // Initial placement position {x, y, z}
    this.dragStartScreen = null;    // Screen coords at drag start {x, y}
    this.dragFace = null;           // Face we planted on (determines extension axes)
    this.dragSourceCuboid = null;   // The cuboid we clicked on (for Y level in projections)
  }

  get name() {
    return 'add';
  }

  /**
   * Handle mouse move - show ghost preview or update drag extension
   * @param {MouseEvent} event
   */
  onMouseMove(event) {
    // If dragging, update the ghost dimensions based on drag
    if (this.isDragging) {
      this.updateDragExtension(event);
      return;
    }

    // Normal preview mode - show ghost at placement position
    const faceEl = event.target.closest('.face');
    if (faceEl && !faceEl.closest('.ghost-preview')) {
      const cuboid = this.getCuboidFromElement(faceEl);
      const face = this.getFaceType(faceEl);

      if (cuboid && face) {
        const position = this.getAdjacentPosition(cuboid, face, event);

        if (position) {
          this.previewPosition = position;
          const isValid = this.collisionManager.isValidPlacement(
            position.x, position.y, position.z, 1, 1, 1, null, this.editor.useNoCollision
          );
          this.ghostPreview.show(
            position.x, position.y, position.z,
            1, 1, 1,
            isValid
          );
          return;
        }
      }
    }

    // Not hovering a face - try ground placement
    const groundPos = this.getGroundPlacement(event);
    if (groundPos) {
      this.previewPosition = groundPos;
      const isValid = this.collisionManager.isValidPlacement(
        groundPos.x, groundPos.y, groundPos.z, 1, 1, 1, null, this.editor.useNoCollision
      );
      this.ghostPreview.show(
        groundPos.x, groundPos.y, groundPos.z,
        1, 1, 1,
        isValid
      );
      return;
    }

    this.ghostPreview.hide();
    this.previewPosition = null;
  }

  /**
   * Update ghost dimensions during drag-to-size operation
   * @param {MouseEvent} event
   */
  updateDragExtension(event) {
    const dims = this.calculateDragDimensions(event);
    if (!dims) return;

    const isValid = this.collisionManager.isValidPlacement(
      dims.x, dims.y, dims.z,
      dims.width, dims.height, dims.depth, null, this.editor.useNoCollision
    );

    this.ghostPreview.show(
      dims.x, dims.y, dims.z,
      dims.width, dims.height, dims.depth,
      isValid
    );
  }

  /**
   * Calculate dimensions based on drag from start position
   * Extends in the plane of the face we planted on
   * @param {MouseEvent} event
   * @return {Object} Dimensions {x, y, z, width, height, depth}
   */
  calculateDragDimensions(event) {
    if (!this.dragStartPosition || !this.dragFace) return null;

    const { x: startX, y: startY, z: startZ } = this.dragStartPosition;

    // If the pointer hasn't moved significantly from drag start, return 1x1x1.
    // This prevents tiny finger movements on touch devices from accidentally
    // producing larger cuboids due to grid-boundary crossings.
    if (this.dragStartScreen) {
      const screenCoords = this.getScreenCoords(event);
      const dx = screenCoords.x - this.dragStartScreen.x;
      const dy = screenCoords.y - this.dragStartScreen.y;
      if (dx * dx + dy * dy < 10 * 10) {
        return { x: startX, y: startY, z: startZ, width: 1, height: 1, depth: 1 };
      }
    }

    const currentPos = this.getCurrentGridPosition(event);
    if (!currentPos) return { x: startX, y: startY, z: startZ, width: 1, height: 1, depth: 1 };

    // Calculate deltas in each axis
    let deltaX = currentPos.x - startX;
    let deltaY = currentPos.y - startY;
    let deltaZ = currentPos.z - startZ;

    // Based on face, determine which axes to extend
    // Top/Bottom: extend X and Z, Y is fixed
    // Front/Back: extend X and Y, Z is fixed
    // Left/Right: extend Y and Z, X is fixed
    // Ground: extend X and Z, Y is fixed

    let x = startX, y = startY, z = startZ;
    let width = 1, height = 1, depth = 1;

    switch (this.dragFace) {
      case 'top':
      case 'bottom':
      case 'ground':
        // Extend in X and Z
        if (deltaX >= 0) {
          width = 1 + deltaX;
        } else {
          x = startX + deltaX;
          width = 1 - deltaX;
        }
        if (deltaZ >= 0) {
          depth = 1 + deltaZ;
        } else {
          z = startZ + deltaZ;
          depth = 1 - deltaZ;
        }
        break;

      case 'front':
      case 'back':
        // Extend in X and Y
        if (deltaX >= 0) {
          width = 1 + deltaX;
        } else {
          x = startX + deltaX;
          width = 1 - deltaX;
        }
        if (deltaY >= 0) {
          height = 1 + deltaY;
        } else {
          y = startY + deltaY;
          height = 1 - deltaY;
        }
        break;

      case 'left':
      case 'right':
        // Extend in Y and Z
        if (deltaY >= 0) {
          height = 1 + deltaY;
        } else {
          y = startY + deltaY;
          height = 1 - deltaY;
        }
        if (deltaZ >= 0) {
          depth = 1 + deltaZ;
        } else {
          z = startZ + deltaZ;
          depth = 1 - deltaZ;
        }
        break;
    }

    return { x, y, z, width, height, depth };
  }

  /**
   * Get current grid position based on face type during drag
   * @param {MouseEvent} event
   * @return {Object|null} Grid position {x, y, z}
   */
  getCurrentGridPosition(event) {
    const screenCoords = this.getScreenCoords(event);
    const renderingYOffset = this.camera.CUBE_SIZE / 2;

    // For horizontal faces (top/bottom/ground), project to the Y plane
    if (this.dragFace === 'top' || this.dragFace === 'bottom' || this.dragFace === 'ground') {
      // Determine the Y level for projection
      let faceWorldY;
      if (this.dragFace === 'ground') {
        faceWorldY = 0;
      } else if (this.dragSourceCuboid) {
        faceWorldY = (this.dragFace === 'top'
          ? this.dragSourceCuboid.y + this.dragSourceCuboid.height
          : this.dragSourceCuboid.y) * this.camera.CUBE_SIZE;
      } else {
        faceWorldY = this.dragStartPosition.y * this.camera.CUBE_SIZE;
      }

      const adjustedScreenY = screenCoords.y - renderingYOffset * this.camera.zoomLevel;
      const worldPos = Transform.screenToWorld(
        screenCoords.x,
        adjustedScreenY,
        this.camera,
        faceWorldY
      );

      return {
        x: Math.floor(worldPos.x / this.camera.CUBE_SIZE),
        y: this.dragStartPosition.y,
        z: Math.floor(-worldPos.z / this.camera.CUBE_SIZE)
      };
    }

    // For vertical faces (front/back/left/right), project to the face plane
    const rotation = Transform.getCameraRotation(this.camera);
    const yScale = Transform.calculateYScale(this.camera.skewAngle);
    const screenXInCameraSpace = screenCoords.x / this.camera.zoomLevel;
    const screenYInCameraSpace = screenCoords.y / this.camera.zoomLevel - renderingYOffset;
    const camX = this.camera.position.x;
    const camZ = this.camera.position.z;
    const camY = this.camera.position.y || 0;

    let worldX, worldY, worldZ;

    if (this.dragFace === 'left' || this.dragFace === 'right') {
      // Fixed X plane - calculate Y and Z
      worldX = this.dragStartPosition.x * this.camera.CUBE_SIZE;
      const relX = worldX - camX;

      if (Math.abs(rotation.sin) > 0.001) {
        const relZ = (relX * rotation.cos - screenXInCameraSpace) / rotation.sin;
        worldZ = relZ + camZ;
      } else {
        worldZ = -this.dragStartPosition.z * this.camera.CUBE_SIZE;
      }

      const relZ = worldZ - camZ;
      worldY = (relX * rotation.sin + relZ * rotation.cos) * yScale - screenYInCameraSpace + camY;

      return {
        x: this.dragStartPosition.x,
        y: Math.floor(worldY / this.camera.CUBE_SIZE),
        z: Math.floor(-worldZ / this.camera.CUBE_SIZE)
      };
    }

    if (this.dragFace === 'front' || this.dragFace === 'back') {
      // Fixed Z plane - calculate X and Y
      worldZ = -this.dragStartPosition.z * this.camera.CUBE_SIZE;
      const relZ = worldZ - camZ;

      if (Math.abs(rotation.cos) > 0.001) {
        const relX = (screenXInCameraSpace + relZ * rotation.sin) / rotation.cos;
        worldX = relX + camX;
      } else {
        worldX = this.dragStartPosition.x * this.camera.CUBE_SIZE;
      }

      const relX = worldX - camX;
      worldY = (relX * rotation.sin + relZ * rotation.cos) * yScale - screenYInCameraSpace + camY;

      return {
        x: Math.floor(worldX / this.camera.CUBE_SIZE),
        y: Math.floor(worldY / this.camera.CUBE_SIZE),
        z: this.dragStartPosition.z
      };
    }

    return null;
  }

  /**
   * Handle mouse down - start drag-to-size operation
   * @param {MouseEvent} event
   */
  onMouseDown(event) {
    if (event.button !== 0) return; // Left click only

    // Determine placement position and face info
    const faceEl = event.target.closest('.face');
    let position, face, sourceCuboid;

    if (faceEl && !faceEl.closest('.ghost-preview')) {
      sourceCuboid = this.getCuboidFromElement(faceEl);
      face = this.getFaceType(faceEl);
      if (sourceCuboid && face) {
        position = this.getAdjacentPosition(sourceCuboid, face, event);
      }
    }

    // Fall back to ground placement
    if (!position) {
      position = this.getGroundPlacement(event);
      face = 'ground';
      sourceCuboid = null;
    }

    if (!position) return;

    // Check collision for initial 1x1x1
    const isValid = this.collisionManager.isValidPlacement(
      position.x, position.y, position.z, 1, 1, 1, null, this.editor.useNoCollision
    );

    if (!isValid) return;

    // Start drag-to-size operation
    this.isDragging = true;
    this.dragStartPosition = { ...position };
    this.dragStartScreen = this.getScreenCoords(event);
    this.dragFace = face;
    this.dragSourceCuboid = sourceCuboid;

    // Show ghost at initial 1x1x1 position
    this.ghostPreview.show(
      position.x, position.y, position.z,
      1, 1, 1,
      true
    );
  }

  /**
   * Calculate placement position based on mouse event
   * @param {MouseEvent} event
   * @return {Object|null} Position {x, y, z} or null
   */
  getPlacementPosition(event) {
    // First check if hovering over a face
    const faceEl = event.target.closest('.face');
    if (faceEl) {
      // Don't place on ghost preview
      if (faceEl.closest('.ghost-preview')) {
        return this.getGroundPlacement(event);
      }

      const cuboid = this.getCuboidFromElement(faceEl);
      if (cuboid) {
        const face = this.getFaceType(faceEl);
        if (face) {
          return this.getAdjacentPosition(cuboid, face, event);
        }
      }
    }

    // Otherwise place on ground plane
    return this.getGroundPlacement(event);
  }

  /**
   * Get position adjacent to a cuboid face at the specific cell being hovered
   * @param {Cuboid} cuboid - The cuboid to place next to
   * @param {string} face - The face being hovered
   * @param {MouseEvent} event - Mouse event for position calculation
   * @return {Object} Position {x, y, z}
   */
  getAdjacentPosition(cuboid, face, event) {
    const screenCoords = this.getScreenCoords(event);

    // IMPORTANT: Cuboid rendering adds a +CUBE_SIZE/2 offset to screen Y position
    // (see Cuboid.js effectiveCenterY calculation). We must compensate for this
    // offset in our inverse projection to get accurate world coordinates.
    const renderingYOffset = this.camera.CUBE_SIZE / 2;

    // For top/bottom faces: find X,Z position on the horizontal face
    if (face === 'top' || face === 'bottom') {
      // Project to the face's Y level (in world pixels)
      const faceWorldY = (face === 'top' ? cuboid.y + cuboid.height : cuboid.y) * this.camera.CUBE_SIZE;

      // Adjust screenY by the rendering offset before inverse projection
      const adjustedScreenY = screenCoords.y - renderingYOffset * this.camera.zoomLevel;

      const worldPos = Transform.screenToWorld(
        screenCoords.x,
        adjustedScreenY,
        this.camera,
        faceWorldY
      );

      // Convert to grid (Z is negated in world coords)
      let gridX = Math.floor(worldPos.x / this.camera.CUBE_SIZE);
      let gridZ = Math.floor(-worldPos.z / this.camera.CUBE_SIZE);

      // Clamp to face bounds
      gridX = Math.max(cuboid.x, Math.min(cuboid.x + cuboid.width - 1, gridX));
      gridZ = Math.max(cuboid.z, Math.min(cuboid.z + cuboid.depth - 1, gridZ));

      return {
        x: gridX,
        y: face === 'top' ? cuboid.y + cuboid.height : cuboid.y - 1,
        z: gridZ
      };
    }

    // For side faces, we project to the vertical face plane.
    // The key insight: screen Y depends on BOTH worldY AND worldZ (via yScale).
    // So we must first calculate the actual worldZ (or worldX) at the click point,
    // then use that to correctly derive worldY from screenY.
    //
    // From worldToScreen:
    //   screenX = ((worldX - camX) * cos - (worldZ - camZ) * sin) * zoom
    //   screenY = (((worldX - camX) * sin + (worldZ - camZ) * cos) * yScale - worldY + camY) * zoom
    //
    // For right/left faces (fixed worldX), solve for worldZ from screenX, then worldY from screenY.
    // For front/back faces (fixed worldZ), solve for worldX from screenX, then worldY from screenY.

    const rotation = Transform.getCameraRotation(this.camera);
    const yScale = Transform.calculateYScale(this.camera.skewAngle);
    const screenXInCameraSpace = screenCoords.x / this.camera.zoomLevel;
    // Subtract renderingYOffset to compensate for Cuboid's screen Y offset
    const screenYInCameraSpace = screenCoords.y / this.camera.zoomLevel - renderingYOffset;
    const camX = this.camera.position.x;
    const camZ = this.camera.position.z;
    const camY = this.camera.position.y || 0;

    let gridX, gridY, gridZ;
    let worldX, worldZ;

    // Return position adjacent to face, clamped to face bounds
    switch (face) {
      case 'right':
      case 'left': {
        // Fixed X plane - solve for Z from screenX, then Y from screenY
        worldX = (face === 'right' ? cuboid.x + cuboid.width : cuboid.x) * this.camera.CUBE_SIZE;
        const relX = worldX - camX;

        if (Math.abs(rotation.sin) > 0.001) {
          // screenX/zoom = relX * cos - relZ * sin
          // relZ = (relX * cos - screenX/zoom) / sin
          const relZ = (relX * rotation.cos - screenXInCameraSpace) / rotation.sin;
          worldZ = relZ + camZ;
        } else {
          // Looking straight at the face, use face center Z
          worldZ = -(cuboid.z + cuboid.depth / 2) * this.camera.CUBE_SIZE;
        }

        gridZ = Math.floor(-worldZ / this.camera.CUBE_SIZE);
        gridZ = Math.max(cuboid.z, Math.min(cuboid.z + cuboid.depth - 1, gridZ));

        // Now calculate Y using the actual worldX and worldZ at click point
        // screenY/zoom = (relX * sin + relZ * cos) * yScale - worldY + camY
        // worldY = (relX * sin + relZ * cos) * yScale - screenY/zoom + camY
        const relZ = worldZ - camZ;
        const worldY = (relX * rotation.sin + relZ * rotation.cos) * yScale - screenYInCameraSpace + camY;
        gridY = Math.floor(worldY / this.camera.CUBE_SIZE);
        gridY = Math.max(cuboid.y, Math.min(cuboid.y + cuboid.height - 1, gridY));

        return {
          x: face === 'right' ? cuboid.x + cuboid.width : cuboid.x - 1,
          y: gridY,
          z: gridZ
        };
      }
      case 'back':
      case 'front': {
        // Fixed Z plane - solve for X from screenX, then Y from screenY
        worldZ = (face === 'back' ? -(cuboid.z + cuboid.depth) : -cuboid.z) * this.camera.CUBE_SIZE;
        const relZ = worldZ - camZ;

        if (Math.abs(rotation.cos) > 0.001) {
          // screenX/zoom = relX * cos - relZ * sin
          // relX = (screenX/zoom + relZ * sin) / cos
          const relX = (screenXInCameraSpace + relZ * rotation.sin) / rotation.cos;
          worldX = relX + camX;
        } else {
          // Looking perpendicular to face, use face center X
          worldX = (cuboid.x + cuboid.width / 2) * this.camera.CUBE_SIZE;
        }

        gridX = Math.floor(worldX / this.camera.CUBE_SIZE);
        gridX = Math.max(cuboid.x, Math.min(cuboid.x + cuboid.width - 1, gridX));

        // Now calculate Y using the actual worldX and worldZ at click point
        const relX = worldX - camX;
        const worldY = (relX * rotation.sin + relZ * rotation.cos) * yScale - screenYInCameraSpace + camY;
        gridY = Math.floor(worldY / this.camera.CUBE_SIZE);
        gridY = Math.max(cuboid.y, Math.min(cuboid.y + cuboid.height - 1, gridY));

        return {
          x: gridX,
          y: gridY,
          z: face === 'back' ? cuboid.z + cuboid.depth : cuboid.z - 1
        };
      }
      default:
        return null;
    }
  }

  /**
   * Get position on ground plane from screen coordinates
   * @param {MouseEvent} event
   * @return {Object} Position {x, y, z}
   */
  getGroundPlacement(event) {
    const screenCoords = this.getScreenCoords(event);

    // Compensate for Cuboid rendering Y offset (same as in getAdjacentPosition)
    const renderingYOffset = this.camera.CUBE_SIZE / 2;
    const adjustedScreenY = screenCoords.y - renderingYOffset * this.camera.zoomLevel;

    // Convert screen to world coordinates on Y=0 plane
    const worldPos = Transform.screenToWorld(
      screenCoords.x,
      adjustedScreenY,
      this.camera,
      0 // worldY = 0 for ground plane
    );

    // Convert from world pixels to grid units
    // Note: Z is negated because Cuboid uses worldZ = -gridZ * cubeSize
    const gridX = Math.floor(worldPos.x / this.camera.CUBE_SIZE);
    const gridZ = Math.floor(-worldPos.z / this.camera.CUBE_SIZE);

    return {
      x: gridX,
      y: 0,
      z: gridZ
    };
  }

  /**
   * Handle mouse up - finalize placement with current dimensions
   * @param {MouseEvent} event
   */
  onMouseUp(event) {
    if (!this.isDragging) return;

    // Get final dimensions
    const dims = this.calculateDragDimensions(event);
    if (!dims) {
      this.cancelDrag();
      return;
    }

    // Check validity
    const isValid = this.collisionManager.isValidPlacement(
      dims.x, dims.y, dims.z,
      dims.width, dims.height, dims.depth, null, this.editor.useNoCollision
    );

    if (isValid) {
      // Get materials from preset (if selected) or use defaults
      const materials = this.editor.getPresetMaterials();

      // Add the cuboid with final dimensions
      this.world.addCuboid(
        dims.x, dims.y, dims.z,
        dims.width, dims.height, dims.depth,
        materials
      );

      // Update the world first - this ensures Android's first-update material refresh
      // happens before triplanar is applied (so triplanar doesn't get overridden)
      this.world.update();

      // If triplanar is enabled and we have a material selected, create a material group
      // This is done AFTER world.update() so triplanar materials are applied last
      if (this.editor.useTriplanar && (this.editor.selectedPreset || this.editor.selectedTexturePath)) {
        const cuboidKey = `${dims.x},${dims.y},${dims.z},${dims.width},${dims.height},${dims.depth}`;
        this.editor.createTriplanarGroup(cuboidKey);
      }

      // Record undo action
      if (this.editor.undoManager) {
        const ux = dims.x, uy = dims.y, uz = dims.z;
        const uw = dims.width, uh = dims.height, ud = dims.depth;
        const uKey = `${ux},${uy},${uz},${uw},${uh},${ud}`;
        // Snapshot materials and group assignments after triplanar group creation
        const cuboid = this.world.cuboids.get(uKey);
        const matSnap = {};
        const groupSnap = {};
        if (cuboid) {
          const faces = ['top', 'bottom', 'front', 'back', 'left', 'right'];
          const gm = this.world.materialGroupManager;
          for (const face of faces) {
            if (cuboid.materials[face]) matSnap[face] = cuboid.materials[face].toJSON();
            if (gm) {
              const group = gm.getGroupForFace(uKey, face);
              if (group) groupSnap[face] = group.id;
            }
          }
        }
        const world = this.world;
        this.editor.undoManager.push({
          description: `Add cuboid ${uw}x${uh}x${ud}`,
          undo: () => {
            const gm = world.materialGroupManager;
            if (gm) {
              for (const face of ['top', 'bottom', 'front', 'back', 'left', 'right']) {
                const group = gm.getGroupForFace(uKey, face);
                if (group) gm.removeFacesFromGroup(group.id, [{ cuboidKey: uKey, face }]);
              }
            }
            world.removeCuboid(ux, uy, uz, uw, uh, ud);
            world.update();
          },
          redo: () => {
            const restored = {};
            for (const [face, data] of Object.entries(matSnap)) {
              restored[face] = Material.fromJSON(data);
            }
            world.addCuboid(ux, uy, uz, uw, uh, ud, restored);
            const gm = world.materialGroupManager;
            if (gm) {
              const groups = new Set();
              for (const [face, groupId] of Object.entries(groupSnap)) {
                gm.assignFacesToGroup(groupId, [{ cuboidKey: uKey, face }]);
                groups.add(groupId);
              }
              groups.forEach(gid => gm.applyGroupMaterial(gid));
            }
            world.update();
          }
        });
      }
    }

    this.cancelDrag();
  }

  /**
   * Cancel the current drag operation
   */
  cancelDrag() {
    this.isDragging = false;
    this.dragStartPosition = null;
    this.dragStartScreen = null;
    this.dragFace = null;
    this.dragSourceCuboid = null;
    this.ghostPreview.hide();
  }

  deactivate() {
    this.cancelDrag();
    super.deactivate();
    this.previewPosition = null;
  }
}
